import { Box, Button, CircularProgress, Paper, TablePagination, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePricing } from '../../contexts/PricingContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import {
  getClientRelevanceContext,
  getUser,
  updateUserPreferences,
} from '../../services/userService';
import type { Tender } from '../../types/tender';
import {
  evaluateTenderRelevance,
  extractCompanyProfileKeywords,
  getTenderKeywordsArray,
  isContextOnlyTerm,
  isMeaningfulKeyword,
  isPhysicalTradeKeyword,
  MAX_AUTO_ACTIVE_KEYWORDS,
  MAX_NEUTRAL_KEYWORDS,
  MIN_RECURRING_HISTORICAL_COUNT,
  normalizeKeyword,
  type RelevanceEvaluation,
  tokensMatch,
} from '../../utils/publicTenderRelevance';
import ArchiveTenderDialog from '../Dialogs/ArchiveTenderDialog';
import PublicTenderCard from './Table/PublicTenderCard';
import PublicTenderDetailsDialog from './Table/PublicTenderDetailsDialog';
import PublicTendersFilters from './Table/PublicTendersFilters';
import RelevanceProfileDialog from './Table/RelevanceProfileDialog';
import { isTenderClosed, parseSubmissionDeadlineInTable } from './Table/tableUtils';

interface PublicTendersTableProps {
  tenders: Tender[];
  onAddTender: (tender: Tender) => Promise<void>;
  onRemoveTender: (tender: Tender, reason: string) => void;
  isLoading: boolean;
  pursuedTendersMap?: Record<string, { ownerName: string; teamName: string; ownerId: string }>;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface UserKeywords {
  [keyword: string]: number;
}

interface ReadStatus {
  [tenderId: string]: boolean;
}

const PublicTendersTable: React.FC<PublicTendersTableProps> = ({
  tenders,
  onAddTender,
  onRemoveTender,
  isLoading,
  pursuedTendersMap,
}) => {
  const { idToken } = useAuth();
  const { account } = useAccount();
  const { pricingSettings } = usePricing();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const effectiveUserId = account?.uid || (account as { id?: string } | null)?.id || '';

  const [filteredTenders, setFilteredTenders] = useState<Tender[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('ALL');
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'llmProcessingTimestamp',
    direction: 'desc',
  });
  const [hasUserManuallySorted, setHasUserManuallySorted] = useState(false);

  const [userKeywords, setUserKeywords] = useState<UserKeywords>({});
  const [customPositiveKeywords, setCustomPositiveKeywords] = useState<string[]>([]);
  const [ignoredKeywords, setIgnoredKeywords] = useState<string[]>([]);
  const [excludedKeywords, setExcludedKeywords] = useState<string[]>([]);
  const [negativeTenderKeywords, setNegativeTenderKeywords] = useState<UserKeywords>({});
  const [companyCapabilityText, setCompanyCapabilityText] = useState<string>('');

  const [viewMode, setViewMode] = useState<'recommended' | 'all'>('all');
  const [hasUserSelectedViewMode, setHasUserSelectedViewMode] = useState(false);
  const [relevanceProfileDialogOpen, setRelevanceProfileDialogOpen] = useState(false);
  const [highlightedProfileKeyword, setHighlightedProfileKeyword] = useState<string | null>(null);

  const [hideClosedTenders, setHideClosedTenders] = useState<boolean>(false);

  const [removeConfirmationDialogOpen, setRemoveConfirmationDialogOpen] = useState(false);
  const [tenderToAction, setTenderToAction] = useState<Tender | null>(null);

  const [isAddingTenderId, setIsAddingTenderId] = useState<string | null>(null);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedTenderForDetails, setSelectedTenderForDetails] = useState<Tender | null>(null);
  const [readStatus, setReadStatus] = useState<ReadStatus>({});

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const companySeedMap = useMemo(
    () => extractCompanyProfileKeywords(companyCapabilityText),
    [companyCapabilityText]
  );

  const hasRelevanceProfile = useMemo(() => {
    return (
      Object.keys(userKeywords).length > 0 ||
      customPositiveKeywords.length > 0 ||
      Object.keys(companySeedMap).length > 0
    );
  }, [userKeywords, customPositiveKeywords, companySeedMap]);

  useEffect(() => {
    let isMounted = true;
    const fetchUserPreferences = async () => {
      if (!effectiveUserId || !account?.clientId) {
        return;
      }
      try {
        const [userData, capabilityText] = await Promise.all([
          getUser(effectiveUserId),
          getClientRelevanceContext(account.clientId).catch(() => ''),
        ]);

        if (!isMounted) {
          return;
        }

        if (capabilityText) {
          setCompanyCapabilityText(capabilityText);
        }

        if (userData) {
          // biome-ignore lint/suspicious/noExplicitAny: Firestore user doc fields
          const uData = userData as any;
          const loadedKeywords: UserKeywords =
            uData.tenderKeywords && typeof uData.tenderKeywords === 'object'
              ? uData.tenderKeywords
              : {};
          const loadedCustomPositive: string[] = Array.isArray(uData.customPositiveKeywords)
            ? uData.customPositiveKeywords
            : [];
          const loadedIgnored: string[] = Array.isArray(uData.ignoredTenderKeywords)
            ? uData.ignoredTenderKeywords
            : [];
          const loadedExcluded: string[] = Array.isArray(uData.excludedTenderKeywords)
            ? uData.excludedTenderKeywords
            : [];
          const loadedNegative: UserKeywords =
            uData.negativeTenderKeywords && typeof uData.negativeTenderKeywords === 'object'
              ? uData.negativeTenderKeywords
              : {};

          if (Object.keys(loadedKeywords).length > 0) {
            setUserKeywords(loadedKeywords);
          }
          if (loadedCustomPositive.length > 0) {
            setCustomPositiveKeywords(loadedCustomPositive);
          }
          if (loadedIgnored.length > 0) {
            setIgnoredKeywords(loadedIgnored);
          }
          if (loadedExcluded.length > 0) {
            setExcludedKeywords(loadedExcluded);
          }
          if (Object.keys(loadedNegative).length > 0) {
            setNegativeTenderKeywords(loadedNegative);
          }

          const hasAnyProfile =
            Object.keys(loadedKeywords).length > 0 ||
            loadedCustomPositive.length > 0 ||
            Boolean(capabilityText);

          if (hasAnyProfile) {
            if (!hasUserManuallySorted) {
              setSortConfig({ key: 'relevance', direction: 'desc' });
            }
          } else {
            setSortConfig((prev) =>
              prev.key === 'relevance' ? { key: 'llmProcessingTimestamp', direction: 'desc' } : prev
            );
          }
          if (uData.defaultPublicTenderSearch) {
            setSearchTerm(uData.defaultPublicTenderSearch);
          }
          if (uData.defaultPublicTenderSource) {
            setSourceFilter(uData.defaultPublicTenderSource);
          }
          if (uData.defaultPublicTenderState) {
            setStateFilter(uData.defaultPublicTenderState);
          }
        } else if (!capabilityText) {
          setSortConfig((prev) =>
            prev.key === 'relevance' ? { key: 'llmProcessingTimestamp', direction: 'desc' } : prev
          );
        }
      } catch (_error) {
        if (isMounted) {
          setSortConfig((prev) =>
            prev.key === 'relevance' ? { key: 'llmProcessingTimestamp', direction: 'desc' } : prev
          );
        }
      }
    };
    fetchUserPreferences();
    return () => {
      isMounted = false;
    };
  }, [effectiveUserId, account?.clientId, hasUserManuallySorted]);

  const handleSaveDefault = async () => {
    if (!effectiveUserId) {
      return;
    }
    try {
      await updateUserPreferences(effectiveUserId, {
        defaultPublicTenderSearch: searchTerm,
        defaultPublicTenderSource: sourceFilter,
        defaultPublicTenderState: stateFilter,
      });
      showSnackbar('Search preferences saved as default.', 'success');
    } catch (_error) {
      showSnackbar('Failed to save search preferences.', 'error');
    }
  };

  const relevanceProfile = useMemo(
    () => ({
      tenderKeywords: userKeywords,
      customPositiveKeywords,
      ignoredKeywords,
      excludedKeywords,
      negativeTenderKeywords,
      companyCapabilityText,
    }),
    [
      userKeywords,
      customPositiveKeywords,
      ignoredKeywords,
      excludedKeywords,
      negativeTenderKeywords,
      companyCapabilityText,
    ]
  );

  const tenderEvaluations = useMemo(() => {
    const evalMap = new Map<string, RelevanceEvaluation>();
    if (!hasRelevanceProfile || !tenders || tenders.length === 0) {
      return evalMap;
    }
    for (const tender of tenders) {
      const tenderKey = tender.tenderId || tender.opportunityID || tender.id;
      if (tenderKey) {
        evalMap.set(tenderKey, evaluateTenderRelevance(tender, relevanceProfile));
      }
    }
    return evalMap;
  }, [hasRelevanceProfile, tenders, relevanceProfile]);

  const getTenderEvaluation = useCallback(
    (tender: Tender): RelevanceEvaluation => {
      const tenderKey = tender.tenderId || tender.opportunityID || tender.id;
      const found = tenderKey ? tenderEvaluations.get(tenderKey) : undefined;
      if (found) {
        return found;
      }
      return {
        score: 0,
        stars: 0,
        isRecommended: false,
        matchedKeywords: [],
        disciplineMatch: false,
        penalizedBy: [],
      };
    },
    [tenderEvaluations]
  );

  const recommendedCount = useMemo(() => {
    let count = 0;
    for (const evalItem of tenderEvaluations.values()) {
      if (evalItem.isRecommended) {
        count += 1;
      }
    }
    return count;
  }, [tenderEvaluations]);

  // Automatically switch to 'recommended' view once when the user's profile loads with matches
  useEffect(() => {
    if (!hasUserSelectedViewMode && hasRelevanceProfile && recommendedCount > 0) {
      setViewMode('recommended');
      setHasUserSelectedViewMode(true);
    }
  }, [hasRelevanceProfile, recommendedCount, hasUserSelectedViewMode]);

  useEffect(() => {
    let newFilteredTenders = [...tenders];

    if (viewMode === 'recommended' && hasRelevanceProfile) {
      newFilteredTenders = newFilteredTenders.filter(
        (tender) => getTenderEvaluation(tender).isRecommended
      );
    }

    if (sourceFilter !== 'ALL') {
      newFilteredTenders = newFilteredTenders.filter((tender) => {
        const tid = tender.tenderId || tender.opportunityID || tender.id || '';
        if (sourceFilter === 'VP') {
          return tid.startsWith('VP');
        }
        if (sourceFilter === 'ATM') {
          return tid.startsWith('ATM');
        }
        if (sourceFilter === 'BNSW') {
          return tid.startsWith('BNSW_');
        }
        if (sourceFilter === 'TVIC') {
          return tid.startsWith('TVIC_');
        }
        if (sourceFilter === 'TWA') {
          return tid.startsWith('TWA_');
        }
        if (sourceFilter === 'SAT') {
          return tid.startsWith('SAT_');
        }
        return true;
      });
    }

    if (stateFilter.length > 0) {
      newFilteredTenders = newFilteredTenders.filter((tender) => {
        const tState = (tender.state || tender.location || '').toUpperCase();
        return stateFilter.some((s) => tState.includes(s)) || tState.includes('NATIONAL');
      });
    }

    if (hideClosedTenders) {
      newFilteredTenders = newFilteredTenders.filter((tender) => {
        const deadlineDateObj = parseSubmissionDeadlineInTable(tender.submissionDeadline || '');
        return !isTenderClosed(deadlineDateObj);
      });
    }

    if (searchTerm.trim()) {
      const orSegments = searchTerm
        .split(/\s+OR\s+/i)
        .map((s) => s.trim())
        .filter(Boolean);
      newFilteredTenders = newFilteredTenders.filter((tender) => {
        const searchableFields = [
          tender.title,
          tender.tenderIssuer,
          tender.tenderId,
          tender.opportunityID,
          tender.id,
          tender.scope,
          tender.description,
          tender.state,
          tender.location,
          ...(Array.isArray(tender.keywords)
            ? tender.keywords
            : String(tender.keywords || '').split(',')),
        ].map((val) => String(val || '').toLowerCase());
        const matchesTerm = (term: string) =>
          searchableFields.some((field) => field.includes(term.toLowerCase()));
        return orSegments.some((orSegment) => {
          const andSegments = orSegment
            .split(/\s+AND\s+/i)
            .map((s) => s.trim())
            .filter(Boolean);
          return andSegments.every((andSegment) => {
            if (andSegment.toUpperCase().startsWith('NOT ')) {
              const negativeTerm = andSegment.substring(4).trim();
              if (!negativeTerm) {
                return true;
              }
              return !matchesTerm(negativeTerm);
            }
            return matchesTerm(andSegment);
          });
        });
      });
    }

    if (sortConfig.key) {
      newFilteredTenders.sort((a, b) => {
        let aValue: unknown, bValue: unknown;
        if (sortConfig.key === 'relevance') {
          aValue = getTenderEvaluation(a).score;
          bValue = getTenderEvaluation(b).score;
          if ((aValue as number) < (bValue as number)) {
            return sortConfig.direction === 'desc' ? 1 : -1;
          }
          if ((aValue as number) > (bValue as number)) {
            return sortConfig.direction === 'desc' ? -1 : 1;
          }
          return 0;
        } else if (sortConfig.key === 'submissionDeadline') {
          const dateA = parseSubmissionDeadlineInTable(a.submissionDeadline || '');
          const dateB = parseSubmissionDeadlineInTable(b.submissionDeadline || '');
          aValue = dateA ? dateA.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
          bValue = dateB ? dateB.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
        } else if (sortConfig.key === 'llmProcessingTimestamp') {
          const dateA = a.llmProcessingTimestamp
            ? a.llmProcessingTimestamp instanceof Date
              ? a.llmProcessingTimestamp
              : new Date(a.llmProcessingTimestamp)
            : null;
          const dateB = b.llmProcessingTimestamp
            ? b.llmProcessingTimestamp instanceof Date
              ? b.llmProcessingTimestamp
              : new Date(b.llmProcessingTimestamp)
            : null;
          aValue = dateA ? dateA.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
          bValue = dateB ? dateB.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
        } else if (sortConfig.key === 'title') {
          aValue = (a.title || 'TitleMissing').toLowerCase();
          bValue = (b.title || 'TitleMissing').toLowerCase();
        } else if (sortConfig.key === 'client') {
          aValue = (a.tenderIssuer || 'IssuerMissing').toLowerCase();
          bValue = (b.tenderIssuer || 'IssuerMissing').toLowerCase();
        } else {
          aValue = a[sortConfig.key as keyof Tender];
          bValue = b[sortConfig.key as keyof Tender];
          if (aValue instanceof Date) {
            aValue = aValue.getTime();
          }
          if (bValue instanceof Date) {
            bValue = bValue.getTime();
          }
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          } else if (Array.isArray(aValue) || typeof aValue === 'object') {
            return 0;
          }
        }
        if (sortConfig.key !== 'relevance') {
          if ((aValue as number) < (bValue as number)) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if ((aValue as number) > (bValue as number)) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }
        return 0;
      });
    }
    setFilteredTenders(newFilteredTenders);
  }, [
    searchTerm,
    sourceFilter,
    stateFilter,
    hideClosedTenders,
    tenders,
    sortConfig,
    viewMode,
    hasRelevanceProfile,
    getTenderEvaluation,
  ]);

  const handleIgnoreKeyword = useCallback(
    async (rawKw: string) => {
      const normalized = normalizeKeyword(rawKw);
      if (!normalized) {
        return;
      }
      const nextIgnored = Array.from(new Set([...ignoredKeywords, normalized]));
      const nextCustomPositive = customPositiveKeywords.filter(
        (k) => normalizeKeyword(k) !== normalized
      );
      const nextExcluded = excludedKeywords.filter((k) => normalizeKeyword(k) !== normalized);
      const nextNegative = { ...negativeTenderKeywords };
      delete nextNegative[normalized];
      delete nextNegative[rawKw];

      setIgnoredKeywords(nextIgnored);
      setCustomPositiveKeywords(nextCustomPositive);
      setExcludedKeywords(nextExcluded);
      setNegativeTenderKeywords(nextNegative);

      if (effectiveUserId) {
        try {
          await updateUserPreferences(effectiveUserId, {
            ignoredTenderKeywords: nextIgnored,
            customPositiveKeywords: nextCustomPositive,
            excludedTenderKeywords: nextExcluded,
            negativeTenderKeywords: nextNegative,
          });
          showSnackbar(`Stopped boosting "${normalized}" (now neutral).`, 'info');
        } catch (_err) {
          showSnackbar('Failed to update keyword preference.', 'error');
        }
      }
    },
    [
      ignoredKeywords,
      customPositiveKeywords,
      excludedKeywords,
      negativeTenderKeywords,
      effectiveUserId,
      showSnackbar,
    ]
  );

  const handleRestoreIgnoredKeyword = useCallback(
    async (rawKw: string) => {
      const normalized = normalizeKeyword(rawKw);
      if (!normalized) {
        return;
      }
      const nextIgnored = ignoredKeywords.filter((k) => normalizeKeyword(k) !== normalized);
      const isSeedOrHistory = Boolean(
        companySeedMap[normalized] || userKeywords[normalized] || userKeywords[rawKw]
      );
      const nextCustomPositive = isSeedOrHistory
        ? customPositiveKeywords
        : Array.from(new Set([...customPositiveKeywords, normalized]));

      setIgnoredKeywords(nextIgnored);
      setCustomPositiveKeywords(nextCustomPositive);

      if (effectiveUserId) {
        try {
          await updateUserPreferences(effectiveUserId, {
            ignoredTenderKeywords: nextIgnored,
            customPositiveKeywords: nextCustomPositive,
          });
          showSnackbar(`Promoted "${normalized}" to active matching.`, 'success');
        } catch (_err) {
          showSnackbar('Failed to promote keyword.', 'error');
        }
      }
    },
    [
      ignoredKeywords,
      companySeedMap,
      userKeywords,
      customPositiveKeywords,
      effectiveUserId,
      showSnackbar,
    ]
  );

  const handleMuteKeyword = useCallback(
    async (rawKw: string) => {
      const normalized = normalizeKeyword(rawKw);
      if (!normalized) {
        return;
      }
      const nextExcluded = Array.from(new Set([...excludedKeywords, normalized]));
      const nextCustomPositive = customPositiveKeywords.filter(
        (k) => normalizeKeyword(k) !== normalized
      );
      const nextIgnored = ignoredKeywords.filter((k) => normalizeKeyword(k) !== normalized);

      setExcludedKeywords(nextExcluded);
      setCustomPositiveKeywords(nextCustomPositive);
      setIgnoredKeywords(nextIgnored);

      if (effectiveUserId) {
        try {
          await updateUserPreferences(effectiveUserId, {
            excludedTenderKeywords: nextExcluded,
            customPositiveKeywords: nextCustomPositive,
            ignoredTenderKeywords: nextIgnored,
          });
          showSnackbar(`Blocked "${normalized}" from Recommended tenders.`, 'info');
        } catch (_err) {
          showSnackbar('Failed to save blocked keyword preference.', 'error');
        }
      }
    },
    [excludedKeywords, customPositiveKeywords, ignoredKeywords, effectiveUserId, showSnackbar]
  );

  const handleUnmuteKeyword = useCallback(
    async (rawKw: string) => {
      const normalized = normalizeKeyword(rawKw);
      if (!normalized) {
        return;
      }
      const nextExcluded = excludedKeywords.filter((k) => normalizeKeyword(k) !== normalized);
      const nextNegative = { ...negativeTenderKeywords };
      delete nextNegative[normalized];
      delete nextNegative[rawKw];

      setExcludedKeywords(nextExcluded);
      setNegativeTenderKeywords(nextNegative);

      if (effectiveUserId) {
        try {
          await updateUserPreferences(effectiveUserId, {
            excludedTenderKeywords: nextExcluded,
            negativeTenderKeywords: nextNegative,
          });
          showSnackbar(`Unblocked "${normalized}".`, 'success');
        } catch (_err) {
          showSnackbar('Failed to update blocked keywords.', 'error');
        }
      }
    },
    [excludedKeywords, negativeTenderKeywords, effectiveUserId, showSnackbar]
  );

  const handleAddPositiveKeyword = useCallback(
    async (rawKw: string) => {
      const normalized = normalizeKeyword(rawKw);
      if (!normalized) {
        return;
      }
      const nextCustomPositive = Array.from(new Set([...customPositiveKeywords, normalized]));
      const nextExcluded = excludedKeywords.filter((k) => normalizeKeyword(k) !== normalized);
      const nextIgnored = ignoredKeywords.filter((k) => normalizeKeyword(k) !== normalized);
      const nextNegative = { ...negativeTenderKeywords };
      delete nextNegative[normalized];
      delete nextNegative[rawKw];

      setCustomPositiveKeywords(nextCustomPositive);
      setExcludedKeywords(nextExcluded);
      setIgnoredKeywords(nextIgnored);
      setNegativeTenderKeywords(nextNegative);
      if (!hasUserManuallySorted) {
        setSortConfig({ key: 'relevance', direction: 'desc' });
      }

      if (effectiveUserId) {
        try {
          await updateUserPreferences(effectiveUserId, {
            customPositiveKeywords: nextCustomPositive,
            excludedTenderKeywords: nextExcluded,
            ignoredTenderKeywords: nextIgnored,
            negativeTenderKeywords: nextNegative,
          });
          showSnackbar(`Added "${normalized}" to your Match Profile.`, 'success');
        } catch (_err) {
          showSnackbar('Failed to save match keyword.', 'error');
        }
      }
    },
    [
      customPositiveKeywords,
      excludedKeywords,
      ignoredKeywords,
      negativeTenderKeywords,
      effectiveUserId,
      hasUserManuallySorted,
      showSnackbar,
    ]
  );

  const handleRemovePositiveKeyword = useCallback(
    async (rawKw: string) => {
      await handleIgnoreKeyword(rawKw);
    },
    [handleIgnoreKeyword]
  );

  const handleAddTender = async (tender: Tender) => {
    if (!idToken) {
      showSnackbar('Authentication session expired. Please refresh.', 'error');
      return;
    }
    setIsAddingTenderId(tender.id || null);
    try {
      await onAddTender(tender);
      showSnackbar(
        `Tender "${tender.title}" added to your dashboard successfully. Generating the capability report now!`,
        'success'
      );
    } catch (_error) {
      // Error handled by parent
    } finally {
      setIsAddingTenderId(null);
    }
  };

  const handleOpenProfileWithKeyword = useCallback((rawKw: string) => {
    const normalized = normalizeKeyword(rawKw);
    setHighlightedProfileKeyword(normalized || rawKw);
    setRelevanceProfileDialogOpen(true);
  }, []);

  const handleOpenDetailsDialog = (tender: Tender) => {
    setSelectedTenderForDetails(tender);
    setDetailsDialogOpen(true);
    if (tender.id) {
      setReadStatus((prev) => ({ ...prev, [tender.id as string]: true }));
    }
  };

  const handleClearIgnoredKeywords = useCallback(async () => {
    setIgnoredKeywords([]);
    if (effectiveUserId) {
      try {
        await updateUserPreferences(effectiveUserId, {
          ignoredTenderKeywords: [],
        });
        showSnackbar('Cleared neutral keywords list.', 'info');
      } catch (_err) {
        showSnackbar('Failed to clear neutral keywords.', 'error');
      }
    }
  }, [effectiveUserId, showSnackbar]);

  const activeCompanySeeds = useMemo(() => {
    const inactiveSet = new Set([
      ...excludedKeywords.map((k) => normalizeKeyword(k)),
      ...ignoredKeywords.map((k) => normalizeKeyword(k)),
    ]);
    return Object.keys(companySeedMap).filter((seed) => !inactiveSet.has(normalizeKeyword(seed)));
  }, [companySeedMap, excludedKeywords, ignoredKeywords]);

  const activePositiveKeywordsList = useMemo(() => {
    const inactiveSet = new Set([
      ...excludedKeywords.map((k) => normalizeKeyword(k)),
      ...ignoredKeywords.map((k) => normalizeKeyword(k)),
    ]);
    const customList: Array<{ keyword: string; count: number; isCustom?: boolean }> = [];
    const seenNorms: string[] = [];

    const isAlreadySeen = (norm: string) =>
      seenNorms.some((existing) => existing === norm || tokensMatch(norm, existing) >= 0.9);

    for (const customKw of customPositiveKeywords) {
      const norm = normalizeKeyword(customKw);
      if (norm && !inactiveSet.has(norm) && !isAlreadySeen(norm)) {
        seenNorms.push(norm);
        customList.push({ keyword: norm, count: 10, isCustom: true });
      }
    }

    const rawHistoricalEntries = Object.entries(userKeywords).filter(
      ([kw, c]) => typeof c === 'number' && c > 0 && isMeaningfulKeyword(kw)
    );
    const hasRecurring = rawHistoricalEntries.some(
      ([kw, c]) => c >= MIN_RECURRING_HISTORICAL_COUNT && !isContextOnlyTerm(kw)
    );
    const hasEstablished = activeCompanySeeds.length > 0 || customList.length > 0 || hasRecurring;
    const minCount = hasEstablished ? MIN_RECURRING_HISTORICAL_COUNT : 1;

    const autoCandidates = new Map<string, number>();

    for (const [kw, count] of rawHistoricalEntries) {
      if (count < minCount) {
        continue;
      }
      const norm = normalizeKeyword(kw);
      if (!norm || inactiveSet.has(norm) || isContextOnlyTerm(norm)) {
        continue;
      }
      if (hasEstablished && isPhysicalTradeKeyword(norm)) {
        continue;
      }
      const matchingSeed = activeCompanySeeds.find(
        (seed) => seed === norm || tokensMatch(norm, seed) >= 0.9
      );
      const canonicalKey = matchingSeed || norm;
      autoCandidates.set(
        canonicalKey,
        Math.max(autoCandidates.get(canonicalKey) || 0, count + (matchingSeed ? 2 : 0))
      );
    }

    for (const seed of activeCompanySeeds) {
      const norm = normalizeKeyword(seed);
      if (!norm || inactiveSet.has(norm) || isContextOnlyTerm(norm)) {
        continue;
      }
      if (!autoCandidates.has(norm)) {
        const isMultiWord = norm.includes(' ');
        autoCandidates.set(norm, isMultiWord ? 3.5 : 2.5);
      }
    }

    const sortedAuto = Array.from(autoCandidates.entries()).sort((a, b) => b[1] - a[1]);

    const autoList: Array<{ keyword: string; count: number; isCustom?: boolean }> = [];
    for (const [norm, score] of sortedAuto) {
      if (autoList.length >= MAX_AUTO_ACTIVE_KEYWORDS) {
        break;
      }
      if (!isAlreadySeen(norm)) {
        seenNorms.push(norm);
        autoList.push({ keyword: norm, count: score });
      }
    }

    return [...customList, ...autoList];
  }, [customPositiveKeywords, userKeywords, excludedKeywords, ignoredKeywords, activeCompanySeeds]);

  const activeIgnoredKeywordsList = useMemo(() => {
    const blockedOrCustomSet = new Set([
      ...excludedKeywords.map((k) => normalizeKeyword(k)),
      ...customPositiveKeywords.map((k) => normalizeKeyword(k)),
    ]);
    const deduplicated: string[] = [];
    const reversed = [...ignoredKeywords].reverse();
    for (const raw of reversed) {
      if (deduplicated.length >= MAX_NEUTRAL_KEYWORDS) {
        break;
      }
      if (!isMeaningfulKeyword(raw)) {
        continue;
      }
      const norm = normalizeKeyword(raw);
      if (!norm || blockedOrCustomSet.has(norm) || isPhysicalTradeKeyword(norm)) {
        continue;
      }
      if (
        !deduplicated.some((existing) => existing === norm || tokensMatch(norm, existing) >= 0.9)
      ) {
        deduplicated.push(norm);
      }
    }
    return deduplicated;
  }, [ignoredKeywords, excludedKeywords, customPositiveKeywords]);

  const negativeArchiveKeywordsList = useMemo(() => {
    const overriddenSet = new Set([
      ...excludedKeywords.map((k) => normalizeKeyword(k)),
      ...ignoredKeywords.map((k) => normalizeKeyword(k)),
      ...customPositiveKeywords.map((k) => normalizeKeyword(k)),
    ]);
    return Object.entries(negativeTenderKeywords)
      .filter(([, count]) => typeof count === 'number' && count > 0)
      .map(([keyword, count]) => ({ keyword: normalizeKeyword(keyword), count }))
      .filter((item) => item.keyword && !overriddenSet.has(item.keyword));
  }, [negativeTenderKeywords, excludedKeywords, ignoredKeywords, customPositiveKeywords]);

  return (
    <Box>
      <PublicTendersFilters
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        sortConfig={sortConfig}
        onSortChange={(e) => {
          const [key, direction] = (e.target.value as string).split('-');
          setSortConfig({ key: key as string, direction: direction as 'asc' | 'desc' });
          setHasUserManuallySorted(true);
          setPage(0);
        }}
        sourceFilter={sourceFilter}
        onSourceFilterChange={(val) => {
          setSourceFilter(val);
          setPage(0);
        }}
        stateFilter={stateFilter}
        onStateFilterChange={(val) => {
          setStateFilter(val);
          setPage(0);
        }}
        hideClosedTenders={hideClosedTenders}
        onHideClosedTendersChange={(val) => {
          setHideClosedTenders(val);
          setPage(0);
        }}
        onSaveSearch={handleSaveDefault}
        hasKeywords={hasRelevanceProfile}
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          setViewMode(mode);
          setHasUserSelectedViewMode(true);
          setPage(0);
        }}
        recommendedCount={recommendedCount}
        totalCount={tenders.length}
        mutedKeywordCount={excludedKeywords.length + negativeArchiveKeywordsList.length}
        onOpenRelevanceProfile={() => {
          setHighlightedProfileKeyword(null);
          setRelevanceProfileDialogOpen(true);
        }}
      />

      {isLoading && filteredTenders.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography ml={2}>Loading public tenders...</Typography>
        </Box>
      )}
      {!isLoading && tenders.length === 0 && (
        <Paper
          sx={{
            textAlign: 'center',
            mt: 3,
            p: 3,
            fontStyle: 'italic',
            background: theme.palette.greyLight,
            color: theme.palette.greyContrast,
            borderRadius: 1,
          }}
        >
          <Typography>Public Tenders is currently empty.</Typography>
        </Paper>
      )}
      {!isLoading && tenders.length > 0 && filteredTenders.length === 0 && (
        <Paper
          sx={{
            textAlign: 'center',
            mt: 3,
            p: 3,
            background: theme.palette.greyLight,
            color: theme.palette.text.primary,
            borderRadius: 2,
          }}
        >
          <Typography sx={{ fontStyle: 'italic', mb: viewMode === 'recommended' ? 1.5 : 0 }}>
            {searchTerm
              ? 'No public tenders match your search criteria.'
              : viewMode === 'recommended'
                ? 'No public tenders match your current Recommended filter.'
                : 'No public tenders match your active filters.'}
          </Typography>
          {viewMode === 'recommended' && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setViewMode('all');
                setHasUserSelectedViewMode(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              View All Tenders ({tenders.length})
            </Button>
          )}
        </Paper>
      )}

      <Box sx={{ mt: 2 }}>
        {(rowsPerPage > 0
          ? filteredTenders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : filteredTenders
        ).map((tender) => {
          const evaluation = getTenderEvaluation(tender);
          return (
            <PublicTenderCard
              key={tender.id}
              tender={tender}
              isRead={!!(tender.id && readStatus[tender.id])}
              isPursued={!!(tender.id && pursuedTendersMap?.[tender.id])}
              isAdding={isAddingTenderId === tender.id}
              isLoading={isLoading}
              {...(tender.id && pursuedTendersMap?.[tender.id]
                ? { pursuedInfo: pursuedTendersMap[tender.id] }
                : {})}
              relevanceScore={evaluation.score}
              relevanceStars={evaluation.stars}
              matchedKeywords={evaluation.matchedKeywords}
              onAddKeyword={handleAddPositiveKeyword}
              onMuteKeyword={hasRelevanceProfile ? handleOpenProfileWithKeyword : undefined}
              onOpenDetails={() => handleOpenDetailsDialog(tender)}
              onAddTender={() => handleAddTender(tender)}
              onArchive={() => {
                setTenderToAction(tender);
                setRemoveConfirmationDialogOpen(true);
              }}
              pricingSettings={pricingSettings}
            />
          );
        })}
      </Box>

      <TablePagination
        component="div"
        count={filteredTenders.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Tenders per page:"
        slotProps={{ select: { variant: 'outlined', size: 'small' } }}
        sx={{
          borderTop: `1px solid ${theme.palette.greyContrast}`,
          mt: 2,
          color: theme.palette.text.primary,
          fontSize: '0.9rem',
          '.MuiTablePagination-toolbar': {
            display: { xs: 'grid', sm: 'flex' },
            gridTemplateColumns: { xs: 'auto auto' },
            gridTemplateRows: { xs: 'auto auto' },
            gridTemplateAreas: { xs: `'label select' 'caption actions'` },
            alignItems: 'center',
            justifyContent: 'flex-end',
            columnGap: { xs: '16px', sm: '8px' },
            rowGap: { xs: '8px', sm: '0px' },
          },
          '.MuiTablePagination-selectLabel': { gridArea: 'label', justifySelf: 'start' },
          '.MuiTablePagination-select': { gridArea: 'select', justifySelf: 'start' },
          '.MuiTablePagination-caption': { gridArea: 'caption', justifySelf: 'start' },
          '.MuiTablePagination-actions': { gridArea: 'actions', justifySelf: 'end' },
          '.MuiTablePagination-spacer': { display: 'none' },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: theme.palette.text.primary,
            fontSize: '0.9rem',
          },
          '& .MuiOutlinedInput-root': { borderRadius: '8px' },
          '& .MuiSelect-icon': { color: theme.palette.text.primary },
          '& .MuiButtonBase-root.Mui-disabled': { color: theme.palette.action.disabled },
        }}
      />

      <ArchiveTenderDialog
        open={removeConfirmationDialogOpen}
        onClose={() => {
          setRemoveConfirmationDialogOpen(false);
          setTenderToAction(null);
        }}
        onConfirm={(params) => {
          if (tenderToAction && params.reason) {
            // Optimistically record negative feedback keywords when archived as not relevant
            const lowerReason = params.reason.toLowerCase();
            if (
              lowerReason.includes('not relevant') ||
              lowerReason.includes('not aligned to capability') ||
              lowerReason.includes('not aligned to strategic')
            ) {
              const tKws = getTenderKeywordsArray(tenderToAction);
              setNegativeTenderKeywords((prev) => {
                const next = { ...prev };
                for (const rawKw of tKws) {
                  const norm = normalizeKeyword(rawKw);
                  if (norm && !isContextOnlyTerm(norm)) {
                    next[norm] = (next[norm] || 0) + 1;
                  }
                }
                return next;
              });
            }
            onRemoveTender(tenderToAction, params.reason);
            setRemoveConfirmationDialogOpen(false);
            setTenderToAction(null);
          } else {
            showSnackbar('Please provide a reason for hiding the tender.', 'warning');
          }
        }}
        tenderTitle={tenderToAction?.title}
        tenderId={tenderToAction?.id}
      />

      <PublicTenderDetailsDialog
        open={detailsDialogOpen}
        tender={selectedTenderForDetails}
        onClose={() => setDetailsDialogOpen(false)}
        onAddTender={handleAddTender}
        onArchive={(tender) => {
          setTenderToAction(tender);
          setRemoveConfirmationDialogOpen(true);
        }}
        isAdding={isAddingTenderId === selectedTenderForDetails?.id}
        isLoading={isLoading}
        isPursued={
          !!(selectedTenderForDetails?.id && pursuedTendersMap?.[selectedTenderForDetails.id])
        }
        pricingSettings={pricingSettings}
      />

      <RelevanceProfileDialog
        open={relevanceProfileDialogOpen}
        onClose={() => {
          setRelevanceProfileDialogOpen(false);
          setHighlightedProfileKeyword(null);
        }}
        highlightedKeyword={highlightedProfileKeyword}
        companySeeds={activeCompanySeeds}
        positiveKeywords={activePositiveKeywordsList}
        ignoredKeywords={activeIgnoredKeywordsList}
        excludedKeywords={excludedKeywords}
        negativeArchiveKeywords={negativeArchiveKeywordsList}
        onAddPositiveKeyword={handleAddPositiveKeyword}
        onRemovePositiveKeyword={handleRemovePositiveKeyword}
        onIgnoreKeyword={handleIgnoreKeyword}
        onRestoreIgnoredKeyword={handleRestoreIgnoredKeyword}
        onClearIgnoredKeywords={handleClearIgnoredKeywords}
        onMuteKeyword={handleMuteKeyword}
        onUnmuteKeyword={handleUnmuteKeyword}
      />
    </Box>
  );
};

export default PublicTendersTable;
